import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { CustomerContacts } from './CustomerContacts';
import { CustomerDetailsForm } from './CustomerDetailsForm';
import { CustomerHistoryTab } from './CustomerHistoryTab';
import { CustomerStatusControl } from './CustomerStatusControl';
import { useCustomerDetail } from './use-customer-detail';

/**
 * A customer's own page: fields and contacts on one tab, history on the other. The
 * first page where nav access (`customer.view`) and mutate access (`customer.manage`)
 * diverge for an existing role, so every edit control here is hidden or disabled for
 * a `customer.view`-only visitor — the server remains the authority either way.
 */
export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const { state, updateError, saving, update, activate, deactivate, addContact, updateContact, removeContact } = useCustomerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('customer.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading customer" />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Customer not found" description="This customer doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this customer" description="Something went wrong loading this customer. Try reloading the page." />
    );
  }

  const { customer } = state;

  return (
    <>
      <PageHeader
        eyebrow="Money"
        title={customer.name}
        description={customer.kraPin}
        actions={<CustomerStatusControl customer={customer} canManage={canManage} onActivate={activate} onDeactivate={deactivate} />}
      />
      <Tabs
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <div className="flex flex-col gap-8">
                <CustomerDetailsForm customer={customer} canManage={canManage} error={updateError} saving={saving} onSave={update} />
                <CustomerContacts contacts={customer.contacts} canManage={canManage} onAdd={addContact} onUpdate={updateContact} onRemove={removeContact} />
              </div>
            ),
          },
          { key: 'history', label: 'History', children: <CustomerHistoryTab customerId={customer.id} /> },
        ]}
      />
    </>
  );
}
